/*
 * ACCOUNT ASSIGNEE AUDIENCE
 *
 * Maps account-team identity to the Admin roster permitted to receive its tasks.
 * Account-team names are normalized so note-linked and direct task flows share one rule.
 * Recent changes: Prospect accounts use the Business Development roster; Commercial uses Commercial.
 */

export interface AccountAudienceTeam {
  teamId: number;
  teamName: string;
}

export interface AccountAudienceMembership {
  userId: number | null;
  teamId: number | null;
}

const ACCOUNT_AUDIENCE_TEAM_NAMES = new Set(['business development', 'commercial']);

export const isAccountAudienceTeam = (teamName?: string | null) => (
  Boolean(teamName && ACCOUNT_AUDIENCE_TEAM_NAMES.has(teamName.trim().toLowerCase()))
);

export const getAccountAudienceTeamIds = (
  accountTeamIds: number[],
  teams: AccountAudienceTeam[],
) => new Set(
  accountTeamIds.filter((teamId) => teams.some((team) => (
    team.teamId === teamId && isAccountAudienceTeam(team.teamName)
  )))
);

export const isAccountAudienceMember = (
  userId: number,
  audienceTeamIds: Set<number>,
  memberships: AccountAudienceMembership[],
) => memberships.some((membership) => (
  membership.userId === userId && audienceTeamIds.has(membership.teamId ?? 0)
));

export const getAccountAudienceMemberIds = (
  audienceTeamIds: Set<number>,
  memberships: AccountAudienceMembership[],
) => Array.from(new Set(memberships.flatMap((membership) => (
  typeof membership.userId === 'number' && audienceTeamIds.has(membership.teamId ?? 0)
    ? [membership.userId]
    : []
))));
